import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProjectStore } from '@/store/projectStore'
import { useAuth } from '@/auth/AuthContext'
import { isRunOnlyAccess } from '@/auth/useProjectPermissions'

/**
 * Guard for builder/design pages (workbench, designers, source editor, deploy).
 *
 * A "run-only" user (every project role is APPLICATION_USER / RUNTIME_USER) must
 * never see the design. If such a user lands on a design route — by direct URL,
 * back button, or a stale link — this redirects them to the Run view of the
 * same project, in the same tab.
 *
 * Pass the project id from the route params so the guard only fires once the
 * loaded access actually belongs to the project on screen (the store nulls
 * `projectAccess` between projects, but this avoids acting on a stale value).
 *
 * The normal "open project" path (the projects picker) launches Run in a *new*
 * tab and never navigates here; this is the defensive backstop.
 */
export function useRunOnlyRedirect(projectFromUrl?: string): void {
  const projectAccess = useProjectStore((s) => s.projectAccess)
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!projectAccess) return
    // Only act when the loaded access is for the project currently on screen.
    if (projectFromUrl && projectAccess.project_id !== projectFromUrl) return
    if (!isRunOnlyAccess(projectAccess.roles, projectAccess.is_owner)) return
    const tenant = user?.tenant_id || '_'
    navigate(
      `/run/${encodeURIComponent(tenant)}/${encodeURIComponent(projectAccess.project_id)}`,
      { replace: true },
    )
  }, [projectAccess, projectFromUrl, user, navigate])
}
